/* museum-chart.js — the folded chart, flat.
 *
 * What this replaces: Cody's three.js chart — a 340×165-segment plane displaced
 * into paper creases, lit by a raking light and a cyan candle, redrawn sixty
 * times a second whether or not anything moved, with its normal and roughness
 * maps baked in the browser at boot. The look is kept; the engine is not. The
 * sheet is one image, baked out of that same renderer through an orthographic
 * camera looking straight down, so the paper, the creases, the torn edge and
 * the light are all his. The table, the candle and the motes are CSS under it.
 *
 * Three rules hold this to what the 3D page actually did:
 *
 *   1. The baked sheet keeps the mesh's UV space, so every `uv` here is the
 *      number it was in 3D: a marker at u,v sits at u × sheetW, v × sheetH.
 *   2. The framing is the camera's, ported: at home the sheet is 0.6407 × the
 *      stage's height tall and 1.32602 × that height wide, whatever the
 *      viewport, centred. On a phone that overflows the width — it did in 3D
 *      too, which is what the stepper is for.
 *   3. Drag is exactly one pixel of sheet per pixel of pointer, with no
 *      inertia, and the wheel pans rather than zooms — both as they were in 3D,
 *      where every zoom level was programmatic (home, a region, a dive) and
 *      pinch did nothing but suspend the drag. **Pinch now zooms**, which is
 *      the one interaction this rebuild adds on purpose (Josh, 2026-09-17):
 *      pinching out fits the whole sheet on a phone screen, which is the thing
 *      the old page could not do at all.
 *
 * And nothing here runs a permanent animation frame: drag writes a transform,
 * flights are CSS transitions, the candle's flicker is a keyframe on its own
 * layer. An idle chart costs nothing.
 */
(function () {
  'use strict';

  if (window.FORUM_CHART) return;   /* shopify:section:load can run this twice */

  var EASE = 'cubic-bezier(.16,.84,.3,1)';
  /* easeInOutCubic, the easing every camera move in the 3D page used. */
  var EASE_IO = 'cubic-bezier(.65,0,.35,1)';

  /* The camera's numbers, kept as the constants they were (museum.js 1453,
     419–420, 548–549): PLANE_W 6.0 over an image aspect of 2.0697, seen down a
     34° lens from a radius of 7.4. */
  var SHEET_H_FRAC = 0.6407;      /* of the stage's height, at home */
  var SHEET_W_FRAC = 1.32602;     /* likewise — width follows height, not the viewport's width */
  var BOOT_ZOOM = 0.787;          /* radius 9.4 → 7.4 */
  var BOOT_MS = 2600;

  function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }
  /* Turn a class on so its transition still runs, without waiting for an
     animation frame: a hidden or backgrounded tab never delivers one, and the
     element would sit at its from-state for ever (the region sheet did exactly
     that in testing). Reading offsetWidth flushes layout, which is all the
     transition needs to have a starting value. */
  function reveal(node, cls) {
    if (!node) return;
    void node.offsetWidth;
    node.classList.add(cls);
  }
  function el(tag, cls, parent) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (parent) parent.appendChild(n);
    return n;
  }
  function quietNow() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function mount(opts) {
    var host = opts.host;
    var sheets = opts.sheets || {};
    var stations = opts.stations || [];
    /* Callbacks are the page's, so they are called behind a guard: a throw in
       a host handler used to stop the chart's own boot dead — the sheet stayed
       at the 0.787 boot zoom and never scaled in. */
    function safely(fn, what) {
      return function () {
        if (typeof fn !== 'function') return;
        try { return fn.apply(null, arguments); }
        catch (err) { if (window.console && console.error) console.error('Chart ' + what + ' handler failed:', err); }
      };
    }
    var onStation = safely(opts.onStation, 'station');
    var onSub = safely(opts.onSub, 'sub');
    var onBlank = safely(opts.onBlank, 'blank');
    var onReady = safely(opts.onReady, 'ready');
    var quiet = quietNow();

    /* ---- state, declared before anything that reads it ------------------
       The furniture below builds the sheet, and the sheet's `sizes` is written
       from the current zoom, so the view has to exist first. */
    var view = { x: 0, y: 0, k: BOOT_ZOOM };
    var sheetW = 0, sheetH = 0;
    var panelW = 0, peek = 0;
    var atHome = true;                 /* until a drag, a pinch or a flight says otherwise */
    var hold = null;                   /* the last programmatic destination, re-applied on resize */
    var kMax = 5.6;                    /* the deepest region view in 3D was 5.481 × home */
    var sheetImgs = [];                /* every sheet, so `sizes` can be rewritten on zoom */

    /* ---- the furniture -------------------------------------------------
       Table, candle and motes are painted, not modelled. The candle sat off
       the scene's bottom-right corner throwing a cyan light (#4dfcff) across
       the paper — which is why the whole chart reads cold. */
    host.classList.add('mc');
    host.innerHTML = '';
    el('div', 'mc__table', host);
    el('div', 'mc__candle', host);
    var motes = el('div', 'mc__motes', host);
    for (var m = 0; m < 14; m++) {
      var mote = el('i', 'mc__mote', motes);
      mote.style.left = (5 + (m * 37) % 90) + '%';
      mote.style.top = (8 + (m * 53) % 84) + '%';
      mote.style.animationDelay = (-m * 1.7) + 's';
      mote.style.animationDuration = (16 + (m % 5) * 3) + 's';
    }

    var viewport = el('div', 'mc__viewport', host);
    var stage = el('div', 'mc__stage', viewport);
    var sheetWorld = sheetImage(sheets.world, 'mc__sheet mc__sheet--world');
    stage.appendChild(sheetWorld);
    var sheetRegion = null;
    var pinLayer = el('div', 'mc__pins', stage);


    function measure() {
      var H = host.clientHeight || window.innerHeight;
      sheetH = H * SHEET_H_FRAC;
      sheetW = H * SHEET_W_FRAC;
      stage.style.width = sheetW + 'px';
      stage.style.height = sheetH + 'px';
    }

    /* The centre of the strip of stage a visitor can actually see: the atlas
       panel covers the left of it on a desktop, and lifts it on a phone. */
    function centre() {
      return {
        x: panelW + (host.clientWidth - panelW) / 2,
        y: host.clientHeight / 2 - host.clientHeight * 0.31 * peek
      };
    }

    function homeView(k) {
      var c = centre();
      k = k || 1;
      return { x: c.x - sheetW * k / 2, y: c.y - sheetH * k / 2, k: k };
    }

    /* One rule per axis, and it is the same rule both ways: an axis the sheet
       overflows may be panned but must stay covered, and an axis with slack is
       centred.

       This is a deliberate departure from the 3D page, whose `mapClampTarget`
       (museum.js 1489–1497) allowed the paper's edge to be dragged 28 % inside
       the frame. On a phone its limit (±456 px) was larger than the sheet's own
       overhang (±351 px), so 105 px of bare table could be pulled into view —
       the survey's §10.3 #6. Zooming made it worse: a half-pinched sheet could
       sit with its left edge 200 px off-screen and bare table down the right.
       Covering instead means the paper is always the thing you are looking at,
       and the moment it fits, it is centred. */
    function clampView(v) {
      var W = host.clientWidth, H = host.clientHeight;
      var c = centre();
      var w = sheetW * v.k, h = sheetH * v.k;
      var cx = v.x + w / 2, cy = v.y + h / 2;
      if (w > (W - panelW)) cx = clamp(cx, W - w / 2, panelW + w / 2);
      else cx = c.x;
      if (h > H) cy = clamp(cy, H - h / 2, h / 2);
      else cy = c.y;
      v.x = cx - w / 2;
      v.y = cy - h / 2;
      return v;
    }

    /* One transform, translate then scale, so the numbers in `view` are screen
       pixels at k = 1 and nothing in here has to unwind a scale. */
    /* How far out a pinch may go: far enough that the whole sheet is on the
       screen with a little air, and never further. On a desktop, where the
       sheet already fits at home, that floor is 1 — there is nothing to
       reveal by zooming out. */
    function zoomFloor() {
      var W = host.clientWidth, H = host.clientHeight;
      if (!sheetW || !sheetH) return 1;
      var fit = Math.min((W - 24) / sheetW, (H - 24) / sheetH);
      return Math.min(1, Math.max(0.2, fit));
    }

    function apply(next, ms, ease) {
      var kWas = view.k;
      view = { x: next.x, y: next.y, k: next.k };
      stage.style.transition = (ms && !quiet)
        ? ('transform ' + ms + 'ms ' + (ease || EASE))
        : 'none';
      stage.style.transform = 'translate3d(' + view.x.toFixed(2) + 'px,' + view.y.toFixed(2) + 'px,0) scale(' + view.k.toFixed(4) + ')';
      host.style.setProperty('--k', view.k.toFixed(4));
      host.style.setProperty('--m', markerScale(view.k).toFixed(4));
      host.style.setProperty('--f', offsetScale(view.k).toFixed(4));
      applySizes();
      if (view.k !== kWas) refold();
    }

    /* The markers keep their size at home and above, as they always have.
       Pinched out past home, which only a phone can do, they shrink with the
       paper down to 0.4 of it. Otherwise nine full-size markers pile into
       each other on a chart 366 px wide (V2.72 K24). */
    function markerScale(k) { return clamp(k, 0.4, 1); }
    /* A marker's `offset` is whole at home and gone by 3× home, where there is
       room for every marker on its own point. Below home it shrinks with the
       marker, so the layout scales as one. */
    function offsetScale(k) { return markerScale(k) * clamp((3 - k) / 2, 0, 1); }

    /* Re-laid out on every size change, and deliberately not gated on having
       had a size before: a stage that is measured at zero — a hidden tab, a
       display:none ancestor, a pane that has not been shown yet — used to
       leave the sheet at 0 × 0 for ever, because the guard that skipped the
       first pass also skipped the one where the size finally arrived. */
    function layout() {
      measure();
      if (!sheetH) return;
      if (atHome) apply(clampView(homeView(view.k)), 0);
      else if (hold) focus(hold.uv, hold.k, 0);
      else apply(clampView(view), 0);
    }

    /* Resolution on demand. `sizes` is what the browser picks a candidate
       with, and it knows nothing about our transform — so scaling the stage to
       3× would go on showing the tier it chose for the unzoomed layout, soft.
       Writing `sizes` from the current zoom makes it re-pick, and it only
       fetches the bigger file at the moment a visitor actually zooms in. */
    function sizesFor(k) {
      var base = (host.clientWidth < 750) ? 96 : 74;
      return Math.min(600, Math.round(base * Math.max(1, k))) + 'vw';
    }
    function applySizes() {
      var v = sizesFor(view.k);
      sheetImgs.forEach(function (img) { if (img.sizes !== v) img.sizes = v; });
    }
    function candidates(src) {
      var out = [];
      if (src.src1536) out.push(src.src1536 + ' 1536w');
      if (src.src) out.push(src.src + ' 2560w');
      if (src.srcHi) out.push(src.srcHi + ' 3072w');
      return out.join(', ');
    }
    function sheetImage(src, cls) {
      var img = el('img', cls);
      img.decoding = 'async';
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');
      img.draggable = false;
      sheetImgs.push(img);
      if (src && src.src) {
        img.srcset = candidates(src);
        img.sizes = sizesFor(view.k);
        img.src = src.src;
      }
      return img;
    }

    /* ---- markers -------------------------------------------------------
       Copied off the live render: a thin near-white outer ring, a fainter
       inner ring and a white diamond over a soft darkening of the paper, so a
       marker sits on the sheet rather than floating over it. The scale is
       undone per marker (`--k`), because zooming the chart should not zoom the
       furniture — in 3D the pick sphere was counter-scaled for the same
       reason.
       Since V2.72 K24 there is one marker per place (or per pair of places
       that are genuinely close), and a marker can carry an `offset`: screen
       pixels at home that keep it off a neighbour, faded out as the chart
       zooms in (see offsetScale). */
    var pins = [];
    var pinOff = [];                   /* markers the filters have emptied */
    function drawPins() {
      subs.forEach(function (s) { s.node.remove(); });
      subs = [];
      pinLayer.innerHTML = '';
      pins = [];
      pinOff = [];
      stations.forEach(function (st, i) {
        var b = el('button', 'mc-pin', pinLayer);
        b.type = 'button';
        b.style.left = (st.uv[0] * 100) + '%';
        b.style.top = (st.uv[1] * 100) + '%';
        if (st.offset) {
          b.style.setProperty('--ox', st.offset[0] + 'px');
          b.style.setProperty('--oy', st.offset[1] + 'px');
        }
        b.setAttribute('aria-label', st.label ? ('Open ' + st.label) : ('Open place ' + (i + 1)));
        b.innerHTML =
          '<span class="mc-pin__shade" aria-hidden="true"></span>' +
          '<span class="mc-pin__halo" aria-hidden="true"></span>' +
          '<span class="mc-pin__ring" aria-hidden="true"></span>' +
          '<span class="mc-pin__inner" aria-hidden="true"></span>' +
          '<span class="mc-pin__glyph" aria-hidden="true"></span>';
        b.addEventListener('click', function (e) { e.stopPropagation(); onStation(i); });
        b.addEventListener('pointerenter', function () { hoverOut('station', i); });
        b.addEventListener('pointerleave', function () { hoverOut('station', null); });
        b.addEventListener('focus', function () { hoverOut('station', i); });
        b.addEventListener('blur', function () { hoverOut('station', null); });
        pins.push(b);
      });
    }

    function hoverOut(what, value) {
      host.dispatchEvent(new CustomEvent(what === 'station' ? 'chart:hover' : 'chart:subhover', {
        detail: what === 'station' ? { index: value } : { key: value }
      }));
    }

    /* ---- sub-pins ------------------------------------------------------
       One per city, drawn at its own place on the sheet (`chartUv` — the real
       spot, read off the engraving), with the bearing ring as the fallback the
       3D page kept for a city with no coordinates. Four rings ripple outward
       on a 6.25 s cycle, as they did in 3D; the parent marker withdraws while
       its cities are out, which is a sequence, not a crossfade.
       A marker that holds two cities only hands over when the zoom has room
       for both (V2.72 K24). Rome and Paestum were 8 px apart at home, so
       pinched back out, the pair folded into their marker again rather than
       stacking two ripples on one spot. No marker holds two since V3.0. */
    var subs = [];
    var FOLD_PX = 38;
    function setSubs(list) {
      subs.forEach(function (s) { s.node.remove(); });
      subs = [];
      (list || []).forEach(function (s) {
        var st = stations[s.parent];
        if (!st) return;
        var uv = s.uv;
        if (!uv) {
          var a = (s.angle || 0) * Math.PI / 180;
          uv = [st.uv[0] + Math.sin(a) * 0.62 / 6.0, st.uv[1] - Math.cos(a) * 0.62 / 2.89893];
        }
        var b = el('button', 'mc-sub', pinLayer);
        b.type = 'button';
        b.style.left = clamp(uv[0], 0.02, 0.98) * 100 + '%';
        b.style.top = clamp(uv[1], 0.02, 0.98) * 100 + '%';
        b.dataset.kind = s.kind || 'photo';
        b.setAttribute('aria-label', s.label ? ('Open ' + s.label) : 'Open stop');
        b.innerHTML =
          '<span class="mc-sub__shade" aria-hidden="true"></span>' +
          '<span class="mc-sub__ripple" aria-hidden="true"><i></i><i></i><i></i><i></i></span>' +
          '<span class="mc-sub__core" aria-hidden="true"></span>';
        b.addEventListener('click', function (e) { e.stopPropagation(); onSub(s.key); });
        b.addEventListener('pointerenter', function () { hoverOut('sub', s.key); });
        b.addEventListener('pointerleave', function () { hoverOut('sub', null); });
        b.addEventListener('focus', function () { hoverOut('sub', s.key); });
        b.addEventListener('blur', function () { hoverOut('sub', null); });
        subs.push({ key: s.key, node: b, parent: s.parent, uv: uv, folded: false });
        reveal(b, 'is-out');
      });
      refold();
    }

    /* Which pairs have room to stand apart at this zoom, and so which
       markers are handed over to their cities. Run on every zoom change and
       every new set of cities; it touches only what changes. */
    function refold() {
      var byParent = {};
      subs.forEach(function (s) { (byParent[s.parent] = byParent[s.parent] || []).push(s); });
      var room = FOLD_PX * markerScale(view.k);
      Object.keys(byParent).forEach(function (p) {
        var group = byParent[p];
        var folded = false;
        for (var a = 0; a < group.length && !folded; a++) {
          for (var b = a + 1; b < group.length; b++) {
            var dx = (group[a].uv[0] - group[b].uv[0]) * sheetW * view.k;
            var dy = (group[a].uv[1] - group[b].uv[1]) * sheetH * view.k;
            if (Math.hypot(dx, dy) < room) { folded = true; break; }
          }
        }
        group.forEach(function (s) {
          s.folded = folded;
          s.node.classList.toggle('is-folded', folded);
          s.node.tabIndex = folded ? -1 : 0;
          s.node.setAttribute('aria-hidden', folded ? 'true' : 'false');
        });
      });
      markerState();
    }

    /* A marker is away while its cities stand in for it, and off while the
       filters have emptied it. Either way it leaves the tab order too: a
       marker nobody can see should not be reachable from the keyboard. */
    function markerState() {
      var out = {};
      subs.forEach(function (s) { if (!s.folded) out[s.parent] = true; });
      pins.forEach(function (p, i) {
        var away = !!out[i];
        var off = !away && !!pinOff[i] && !subs.some(function (s) { return s.parent === i; });
        p.classList.toggle('is-away', away);
        p.classList.toggle('is-off', off);
        p.tabIndex = (away || off) ? -1 : 0;
        p.setAttribute('aria-hidden', (away || off) ? 'true' : 'false');
      });
    }

    /* ---- flights -------------------------------------------------------
       Everything the 3D page did with a camera — frame a region, come home,
       dive at a stop — is this one move with different arguments. */
    function focus(uv, k, ms) {
      var c = centre();
      k = k || view.k;
      hold = { uv: uv, k: k };
      atHome = false;
      apply(clampView({ x: c.x - uv[0] * sheetW * k, y: c.y - uv[1] * sheetH * k, k: k }),
            ms === 0 ? 0 : (ms || 900), EASE_IO);
    }

    function goHome(ms, k) {
      atHome = true;
      hold = null;
      apply(clampView(homeView(k || 1)), ms === 0 ? 0 : (ms || 900), EASE_IO);
    }

    /* Frame a set of points: centred on their middle, as deep as `maxK` if
       they fit and shallower if they do not (V2.72 K24). A country used to be
       one hand-set frame, which could only suit one screen. The US now runs
       from San Diego to Lake George, which is a 2× frame in a phone's strip
       above the sheet and a 3.4× one beside the desktop panel. `top` and
       `bottom` bound the strip the panel leaves visible; the sides are the
       panel's and the screen's. */
    function fitTo(uvs, o, ms) {
      if (!uvs || !uvs.length || !sheetW) return;
      o = o || {};
      var u0 = Infinity, u1 = -Infinity, v0 = Infinity, v1 = -Infinity;
      uvs.forEach(function (p) {
        u0 = Math.min(u0, p[0]); u1 = Math.max(u1, p[0]);
        v0 = Math.min(v0, p[1]); v1 = Math.max(v1, p[1]);
      });
      var c = centre();
      var pad = o.pad === undefined ? 64 : o.pad;
      var top = o.top === undefined ? 0 : o.top;
      var bottom = o.bottom === undefined ? host.clientHeight : o.bottom;
      var halfW = Math.min(c.x - panelW, host.clientWidth - c.x) - pad;
      var halfH = Math.min(c.y - top, bottom - c.y) - pad;
      var k = o.maxK || kMax;
      if (u1 > u0) k = Math.min(k, halfW / ((u1 - u0) / 2 * sheetW));
      if (v1 > v0) k = Math.min(k, halfH / ((v1 - v0) / 2 * sheetH));
      focus([(u0 + u1) / 2, (v0 + v1) / 2], clamp(k, o.minK || 1, kMax), ms);
    }

    /* ---- the close-up sheets ------------------------------------------
       Only the regions that have one; the rest zoom the world sheet, which is
       what Italy always did. The swap waits for the dissolve to be at rest, or
       region → region cuts through black. */
    var regionShown = null, regionPending = null, regionTimer = 0;
    function setRegionSheet(i) {
      if (i === null || i === undefined || !(sheets.regions && sheets.regions[i])) {
        regionPending = null;
        if (sheetRegion) sheetRegion.classList.remove('is-in');
        regionShown = null;
        return;
      }
      if (regionShown === i) return;
      if (!sheetRegion) {
        sheetRegion = sheetImage(null, 'mc__sheet mc__sheet--region');
        stage.insertBefore(sheetRegion, pinLayer);
      }
      if (regionShown !== null) {
        /* something else is up: fade it out first, then swap the source */
        regionPending = i;
        sheetRegion.classList.remove('is-in');
        regionShown = null;
        clearTimeout(regionTimer);
        regionTimer = setTimeout(function () {
          var want = regionPending;
          regionPending = null;
          if (want !== null) setRegionSheet(want);
        }, quiet ? 0 : 640);
        return;
      }
      var conf = sheets.regions[i];
      sheetRegion.srcset = candidates(conf);
      sheetRegion.sizes = sizesFor(view.k);
      sheetRegion.src = conf.src;
      regionShown = i;
      reveal(sheetRegion, 'is-in');
    }

    /* ---- drag, pinch, wheel --------------------------------------------
       Drag is 1:1 with no inertia, as it was in 3D. Pinch is new (Josh,
       2026-09-17: "drag is good but i want to zoom out too"): two fingers
       scale the sheet about the point between them, and pinching all the way
       out fits the whole chart on the screen — which on a phone, where the
       sheet is three times the width of the screen at home, is the thing that
       was missing. */
    var drag = null, down = null;
    var live = {};                 /* pointerId -> {x, y} for every finger down */
    var pinch = null;

    function fingers() { return Object.keys(live); }

    viewport.addEventListener('pointerdown', function (e) {
      live[e.pointerId] = { x: e.clientX, y: e.clientY };
      if (e.target.closest('.mc-pin, .mc-sub')) return;
      var ids = fingers();
      if (ids.length > 1) {
        /* A second finger ends the drag and starts a pinch. */
        drag = null;
        host.classList.remove('is-dragging');
        pinch = pinchState(ids);
        return;
      }
      down = { x: e.clientX, y: e.clientY, moved: false };
      drag = { x: e.clientX, y: e.clientY, vx: view.x, vy: view.y };
      try { viewport.setPointerCapture(e.pointerId); } catch (err) {}
      host.classList.add('is-dragging');
    });

    function pinchState(ids) {
      var a = live[ids[0]], b = live[ids[1]];
      var dist = Math.hypot(a.x - b.x, a.y - b.y);
      return {
        dist: dist < 1 ? 1 : dist,
        k: view.k,
        mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        vx: view.x, vy: view.y
      };
    }

    viewport.addEventListener('pointermove', function (e) {
      if (!live[e.pointerId]) return;
      live[e.pointerId] = { x: e.clientX, y: e.clientY };
      var ids = fingers();

      if (ids.length > 1) {
        if (!pinch) pinch = pinchState(ids);
        var a = live[ids[0]], b = live[ids[1]];
        var dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
        var k = clamp(pinch.k * (dist / pinch.dist), zoomFloor(), kMax);
        /* Hold the point between the fingers still, so the paper scales under
           them rather than sliding away from them. */
        var sx = (pinch.mid.x - pinch.vx) / pinch.k;
        var sy = (pinch.mid.y - pinch.vy) / pinch.k;
        var mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        atHome = false;
        hold = null;
        apply(clampView({ x: mid.x - sx * k, y: mid.y - sy * k, k: k }), 0);
        return;
      }

      if (!drag) return;
      if (Math.abs(e.clientX - down.x) > 4 || Math.abs(e.clientY - down.y) > 4) down.moved = true;
      atHome = false;
      hold = null;
      apply(clampView({ x: drag.vx + (e.clientX - drag.x), y: drag.vy + (e.clientY - drag.y), k: view.k }), 0);
    });

    function up(e) {
      delete live[e.pointerId];
      if (fingers().length < 2) pinch = null;
      /* The finger left over after a pinch must not become a drag that jumps
         the sheet, so a drag is only re-armed on the next pointerdown. */
      if (fingers().length) { drag = null; return; }
      /* Every gesture ends with a settle: the clamp is applied once more, with
         a short ease, so nothing can be left resting out of bounds — however
         the fingers left the glass. It is a no-op on a view that is already
         where it should be. */
      apply(clampView(view), quiet ? 0 : 200, EASE);
      if (!drag) return;
      drag = null;
      host.classList.remove('is-dragging');
      /* A press on bare paper with no travel clears the region, as it did. */
      if (down && !down.moved && !e.target.closest('.mc-pin, .mc-sub')) onBlank();
    }
    viewport.addEventListener('pointerup', up);
    viewport.addEventListener('pointercancel', up);
    viewport.addEventListener('lostpointercapture', function (e) { delete live[e.pointerId]; });

    viewport.addEventListener('wheel', function (e) {
      if (e.ctrlKey) return;               /* leave the browser's own page zoom alone */
      e.preventDefault();
      atHome = false;
      hold = null;
      apply(clampView({ x: view.x - e.deltaX, y: view.y - e.deltaY, k: view.k }), 0);
    }, { passive: false });

    /* Keyboard: the chart had none at all — its markers were pixels in a
       canvas, so nothing on it could be reached without a pointer. The markers
       are buttons now, and the arrows push the paper. Bound on the document
       because the chart is the page: with nothing focused there would otherwise
       be no keyboard at all. It stands aside for a field, for a modifier
       combination, and for anything covering the chart. */
    function onKey(e) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      var t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName || ''))) return;
      var b = document.body.classList;
      if (b.contains('hang-open') || b.contains('film-open') || b.contains('season-open')) return;
      if (document.getElementById('atlasPanel') &&
          document.getElementById('atlasPanel').classList.contains('open') &&
          t && t.closest && t.closest('#atlasPanel')) return;
      var step = e.shiftKey ? 240 : 90;
      var dx = 0, dy = 0;
      if (e.key === 'ArrowLeft') dx = step;
      else if (e.key === 'ArrowRight') dx = -step;
      else if (e.key === 'ArrowUp') dy = step;
      else if (e.key === 'ArrowDown') dy = -step;
      else return;
      e.preventDefault();
      atHome = false;
      hold = null;
      apply(clampView({ x: view.x + dx, y: view.y + dy, k: view.k }), 260, EASE);
      host.dispatchEvent(new CustomEvent('chart:keypan'));
    }
    document.addEventListener('keydown', onKey);

    /* ---- ready and boot ------------------------------------------------
       The splash waits for the sheet to be decoded, not merely fetched: a
       decoded image is the first thing anyone can actually see. Then the
       chart arrives the way it did in 3D — from 0.787 × to 1 × over 2.6 s. */
    function whenReady(cb) {
      var done = false;
      function fire() { if (!done) { done = true; cb(); } }
      if (sheetWorld.complete && sheetWorld.naturalWidth) {
        (sheetWorld.decode ? sheetWorld.decode().catch(function () {}) : Promise.resolve()).then(fire);
      } else {
        sheetWorld.addEventListener('load', function () {
          (sheetWorld.decode ? sheetWorld.decode().catch(function () {}) : Promise.resolve()).then(fire);
        });
        sheetWorld.addEventListener('error', fire);   /* a missing sheet must not hold the page */
      }
      setTimeout(fire, 8000);
    }

    measure();
    drawPins();
    apply(homeView(BOOT_ZOOM), 0);
    /* Both, on purpose. The observer is the accurate one — it sees the stage
       change for reasons the window never hears about, like the atlas panel
       resizing it — but its callbacks are delivered before a paint, and a tab
       that is not being painted (hidden, backgrounded, restored from the back
       button) never gets one. The window event is the coarse backstop that
       covers a rotation or a resize in that state. `layout()` is idempotent,
       so being called twice costs a measure. */
    var ro = null;
    if (window.ResizeObserver) {
      ro = new ResizeObserver(layout);
      ro.observe(host);
    }
    window.addEventListener('resize', layout);
    window.addEventListener('orientationchange', layout);
    whenReady(function () {
      host.classList.add('is-ready');
      onReady();
      requestAnimationFrame(function () {
        if (atHome) apply(clampView(homeView(1)), BOOT_MS, EASE_IO);
      });
    });

    return {
      focus: focus,
      home: goHome,
      fitTo: fitTo,
      setStations: function (list) { stations = list || []; drawPins(); },
      setSubs: setSubs,
      setRegionSheet: setRegionSheet,
      /* One marker, several (a country's), or none. */
      highlightStation: function (i) {
        var on = i === null || i === undefined ? [] : [].concat(i);
        pins.forEach(function (p, n) { p.classList.toggle('is-live', on.indexOf(n) !== -1); });
      },
      highlightSub: function (key) {
        subs.forEach(function (s) { s.node.classList.toggle('is-live', s.key === key); });
      },
      /* `mask[i]` false takes marker i off the chart: the filters have left
         nothing in it (V2.72 K24). */
      showStations: function (mask) {
        pinOff = (mask || []).map(function (on) { return !on; });
        markerState();
      },
      /* Whether a city stands on the chart as itself right now: out, and not
         folded back into its marker. */
      subShown: function (key) {
        return subs.some(function (s) { return s.key === key && !s.folded; });
      },
      setPanelWidth: function (px) {
        panelW = px || 0;
        host.style.setProperty('--panel-w', panelW + 'px');
        if (atHome) apply(clampView(homeView(view.k)), 950, EASE_IO);
        else if (hold) focus(hold.uv, hold.k, 950);
      },
      setPeek: function (fraction) {
        peek = fraction || 0;
        if (atHome) apply(clampView(homeView(view.k)), 950, EASE_IO);
        else if (hold) focus(hold.uv, hold.k, 950);
      },
      /* Where a point of the sheet is on screen right now — the tooltip and
         the stepper's marker light need it, and it is the one thing the old
         page got from `camera.project`. */
      pointScreen: function (uv) {
        return { x: view.x + uv[0] * sheetW * view.k, y: view.y + uv[1] * sheetH * view.k };
      },
      /* Where a marker is drawn, which is its point plus whatever share of
         its `offset` this zoom keeps. */
      stationPoint: function (i) {
        var st = stations[i];
        if (!st) return null;
        var f = offsetScale(view.k);
        var o = st.offset || [0, 0];
        return {
          x: view.x + st.uv[0] * sheetW * view.k + o[0] * f,
          y: view.y + st.uv[1] * sheetH * view.k + o[1] * f
        };
      },
      zoom: function () { return view.k; },
      sheetSize: function () { return { w: sheetW, h: sheetH }; },
      quiet: function () { return quiet; },
      destroy: function () {
        if (ro) ro.disconnect();
        window.removeEventListener('resize', layout);
        window.removeEventListener('orientationchange', layout);
        document.removeEventListener('keydown', onKey);
        clearTimeout(regionTimer);
        host.innerHTML = '';
        host.classList.remove('mc', 'is-ready');
      }
    };
  }

  window.FORUM_CHART = { mount: mount, version: '1' };
})();
